import { NoteType } from '../../enums/NoteType'
import Note from '../Note'

type Props = {
    setQuery: (query: string) => void
    setValidationMessage: (message: string | null) => void
}

const BooleanSearchInstructions: React.FC<Props> = ({
    setQuery,
    setValidationMessage,
}) => {
    const queryTemplates = [
        {
            label: 'Cloud experts with AWS',
            query: 'summary:cloud AND skills:AWS',
        },
        {
            label: 'Exact phrase: software engineer',
            query: 'headline:"software engineer"',
        },
        {
            label: 'People with either AWS or GCP',
            query: 'skills:(AWS OR GCP)',
        },
        {
            label: 'Exclude interns',
            query: 'summary:cloud NOT intern',
        },
        {
            label: 'Fuzzy developer match',
            query: 'developer~',
        },
        {
            label: "All current CTOs that haven't been CTOs in the past",
            query: 'currentJobTitle:CTO AND NOT pastJobTitle:CTO',
        },
    ]

    const handleTemplateClick = (template: string) => {
        setQuery(template)
        setValidationMessage(null)
    }

    return (
        <div className="flex flex-col w-[30%] mr-4 border-r-2 pr-4">
            <div className="mt-4 space-y-2">
                <Note type={NoteType.INFO}>
                    <p className="font-semibold">How to search:</p>
                    <div className="space-y-2 pl-2 text-sm text-gray-800">
                        <div>
                            <span className="font-semibold">
                                Single keyword:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                cloud
                            </code>{' '}
                            – finds profiles mentioning "cloud" in any field.
                        </div>
                        <div>
                            <span className="font-semibold">
                                Multiple terms:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                cloud aws gcp azure devops
                            </code>{' '}
                            – matches only profiles that include <em>all</em> of
                            these terms in any field. Effectively adding{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                AND
                            </code>{' '}
                            between them.
                        </div>
                        <div>
                            <span className="font-semibold">Exact phrase:</span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                "software engineer"
                            </code>{' '}
                            – matches the exact phrase in order, even if it's
                            part of a longer sentence.
                        </div>
                        <div>
                            <span className="font-semibold">
                                Field-specific search:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                headline:developer
                            </code>{' '}
                            or{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                summary:"data engineer"
                            </code>
                        </div>
                        <div className="pl-2">
                            <p className="font-semibold mt-2">
                                Available fields:
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-gray-800">
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        firstName
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        lastName
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        fullName
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        linkedInUrl
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        headline
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        summary
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        skills
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        location
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        industry
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        jobDescription
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        jobTitle
                                    </code>
                                    <span className="ml-2">
                                        all job titles (past and current)
                                    </span>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        company
                                    </code>
                                    <span className="ml-2">
                                        all companies (past and current)
                                    </span>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        currentJobTitle
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        currentCompany
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        pastJobTitle
                                    </code>
                                </li>
                                <li>
                                    <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                        pastCompany
                                    </code>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <span className="font-semibold">
                                Combine terms:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                cloud AND AWS
                            </code>{' '}
                            – must match both. Use{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                OR
                            </code>{' '}
                            for either.
                        </div>
                        <div>
                            <span className="font-semibold">
                                Grouped search:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                skills:(AWS OR GCP)
                            </code>{' '}
                            – matches if any listed skill is present.
                        </div>
                        <div>
                            <span className="font-semibold">Fuzzy match:</span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                develop~
                            </code>{' '}
                            – matches similar terms like "developer" or
                            "development".
                        </div>
                        <div>
                            <span className="font-semibold">
                                Boost relevance:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                cloud^3
                            </code>{' '}
                            – increases importance of "cloud" in ranking
                            results.
                        </div>
                        <div>
                            <span className="font-semibold">
                                Exclude terms:
                            </span>{' '}
                            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
                                cloud NOT intern
                            </code>{' '}
                            – includes "cloud" but excludes results mentioning
                            "intern".
                        </div>
                    </div>
                </Note>
            </div>

            <div className="mt-4">
                <p className="font-semibold mb-2">Try one of these examples:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {queryTemplates.map((t, i) => (
                        <button
                            key={i}
                            onClick={() => handleTemplateClick(t.query)}
                            className="text-left border border-gray-300 p-2 rounded hover:bg-gray-100 transition"
                        >
                            <div className="font-medium text-gray-800">
                                {t.label}
                            </div>
                            <code className="text-gray-600 text-xs block mt-1">
                                {t.query}
                            </code>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    )
}
export default BooleanSearchInstructions
